// src/components/reports/CompanyTrendReport/index.tsx

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { ReportUnit } from '../../../../types/sales';
import { salesReportService } from '../../../../services/salesReportService';
import { useAuth } from 'react-oidc-context';
import { useReportContext } from '../../../../contexts/ReportContext';
import { SummaryCards } from './SummaryCards';
import { TrendTabs } from './TrendTabs';
import { TrendChart } from './TrendChart';
import { TrendTable } from './TrendTable';

interface CompanyTrendReportProps {
  startMonth: string;
  endMonth: string;
  unit: ReportUnit;
}

export const CompanyTrendReport: React.FC<CompanyTrendReportProps> = ({
  startMonth,
  endMonth,
  unit
}) => {
  const auth = useAuth();
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

  // ReportContextから必要な関数を取得
  const {
    isCacheValid,
    getCachedData,
    cacheReportData,
    markReportAsDisplayed
  } = useReportContext();

  // キャッシュのチェック
  const cacheKey = 'company-trend';
  const shouldFetch = !isCacheValid(cacheKey);
  const cachedData = getCachedData(cacheKey);

  // React Queryを使用してデータ取得
  const { data, isLoading, error } = useQuery({
    queryKey: ['companyTrendReport', { startMonth, endMonth, unit }],
    queryFn: async () => {
      const token = auth.user?.access_token;
      return await salesReportService.getCompanyTrendReport(
        startMonth,
        endMonth,
        unit,
        token
      );
    },
    enabled: shouldFetch || !cachedData, // キャッシュが無効な場合のみフェッチ
    staleTime: 5 * 60 * 1000, // 5分間はデータをstaleと見なさない
  });

  // データ取得成功時の処理
  useEffect(() => {
    if (data) {
      // データをコンテキストのキャッシュに保存
      cacheReportData(cacheKey, data);
      // レポートが表示されたことをマーク
      markReportAsDisplayed();
    }
  }, [data, cacheReportData, cacheKey, markReportAsDisplayed]);

  // エラー発生時の処理
  useEffect(() => {
    if (error) {
      console.error('会社全体トレンドレポートの取得に失敗しました:', error);
    }
  }, [error]);

  // 表示するデータを決定（キャッシュまたは新たに取得したデータ）
  const reportData = (shouldFetch || !cachedData) ? data : cachedData;

  // 表示モード切り替えのハンドラー
  const handleViewModeChange = (newValue: 'chart' | 'table') => {
    setViewMode(newValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        データの取得中にエラーが発生しました
      </Alert>
    );
  }

  if (!reportData || reportData.length === 0) {
    return (
      <Alert severity="info">
        該当するデータがありません。条件を変更して再度お試しください。
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        会社全体トレンドレポート
      </Typography>

      <SummaryCards reportData={reportData} startMonth={startMonth} endMonth={endMonth} />

      <TrendTabs viewMode={viewMode} onViewModeChange={handleViewModeChange} />

      {viewMode === 'chart' ? (
        <TrendChart
          data={reportData}
          title={`売上・利益推移 (${startMonth} 〜 ${endMonth})`}
        />
      ) : (
        <TrendTable data={reportData} />
      )}
    </Box>
  );
};